import React from 'react';
import moment from 'moment';
import { useDispatch } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { LinearProgress, Typography } from '@mui/material';
import { PieChart, type PieSeries } from '@mui/x-charts';

import { Icon, Utils, useSettings } from 'app/main/components';
import { DetailsSectionWrapper } from 'app/main/components/page-layouts';

import { PhoneCallsChartProps } from './PhoneCallsChart';
import { PhonceCallsChartYearSelector } from './PhoneCallsChartsUtils';

interface PhoneCallsPieChartProps extends PhoneCallsChartProps {
	title: string;
	field: string;
	columns: string[];
	formatGroupLabel: (group: any) => string;
}

export default function PhoneCallsPieChart(props: PhoneCallsPieChartProps) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const currentYear = Utils.today().year();

	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [year, setYear] = React.useState<number>(currentYear);
	const [results, setResults] = React.useState<any>([]);
	const [totals, setTotals] = React.useState<any>({});

	React.useEffect(() => {
		setIsLoading(true);
		Utils.executeStored({
			action: 'st_sys_statstable_list',
			data: {
				view: 'v_phonecalls_stats',
				field: props.field,
				columns: props.columns,
				n_page: 1,
				n_rows: 30,
				filters: [
					...props.filters,
					{
						filt_field: 'phca_date',
						filt_clause: 'range',
						filt_value: { from: moment([year]).startOf('year'), to: moment([year]).endOf('year') }
					}
				]
			},
			dispatch,
			successHandler: response => {
				setResults(response.data.list || []);
				setTotals(response.data.totals || {});
				setIsLoading(false);
			}
		});
	}, [dispatch, props.columns, props.field, props.filters, year]);

	const series: PieSeries[] = [
		{
			data: results.map((group, index) => ({
				id: index,
				label: props.formatGroupLabel(group),
				value: group.phca_duration
			})),
			innerRadius: '50%',
			sortingValues: 'asc',
			highlightScope: { highlight: 'item', fade: 'global' },
			valueFormatter: ({ value }) => Utils.formatDuration({ seconds: value })
		}
	];

	return (
		<DetailsSectionWrapper
			title={props.title}
			subheader={
				<>
					{Object.keys(totals).map((value, i) => (
						<Typography key={i} variant="subtitle2">
							<FormattedMessage
								id="phoneCallsPieChart.total"
								defaultMessage={t(`PHONECALLS_CHART_TOTAL_${value.toUpperCase()}`)}
								values={{ value: Utils.formatDuration({ seconds: totals[value] }) }}
							/>
						</Typography>
					))}
				</>
			}
			avatar={<Icon icon="chart-pie" />}
			headerAction={<PhonceCallsChartYearSelector year={year} setYear={setYear} />}
		>
			{isLoading ? (
				<LinearProgress variant="query" />
			) : (
				<PieChart
					className="h-full w-full"
					width={200}
					height={100}
					margin={{ right: 0 }}
					series={series}
					hideLegend
				/>
			)}
		</DetailsSectionWrapper>
	);
}
